import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  AppBar,
  Toolbar,
  Grid,
  Icon, // Import Icon for device placeholders
  IconButton, // Add this for the password visibility toggle
  Collapse // Add Collapse import
} from '@mui/material';
import LaptopMacIcon from '@mui/icons-material/LaptopMac'; // Example icon
import TvIcon from '@mui/icons-material/Tv'; // Example icon
import TabletMacIcon from '@mui/icons-material/TabletMac'; // Example icon
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'; // Example icon
import VisibilityIcon from '@mui/icons-material/Visibility'; // Import visibility icon
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'; // Import visibility off icon
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthService } from '../services/authService';
import { createSubscription, createCustomer, updateUserPlanType } from '../services/stripeService';
// tracking removed
import airtableEmailService from '../services/airtableEmailService'; // Import Airtable email service
import { PRICE_IDS, PLANS } from '../services/PriceIdService'; // Import from the new service
import { landingSettings, isPromoEnabled, getPromoCode, getPromoTrialDays, getPromoDaysRemaining, isValidPromo } from '../services/landing-settings'; // Import landing settings
import headerLogo from '../img/headerlogo.png'; // Import the logo directly
import logo from '../img/signup-popcorn.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Import CheckCircleIcon
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import StarIcon from '@mui/icons-material/Star';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import device1 from '../img/device-1.jpg';
import device2 from '../img/device-2.jpg';
import device3 from '../img/device-3.jpg';
import device4 from '../img/device-4.jpg';
import device5 from '../img/device-5.jpg';
import device6 from '../img/device-6.jpg';

// Feature flag to toggle Free tier
const FREE_TIER_ENABLED = true;

// --- Configuration ---
// Add the new "Setup" step at the beginning
const steps = ['Setup', 'Create Account', 'Verify Email', 'Choose Plan', 'Payment'];

// --- Helper Components ---

const StepContentWrapper = ({ children }) => (
  <Box
    sx={{
      p: { xs: 3, sm: 4 },
      mt: 3,
      mb: 3,
      bgcolor: 'transparent',
      borderRadius: 0,
      boxShadow: 'none',
      maxWidth: '600px',
      mx: 'auto'
    }}
  >
    {children}
  </Box>
);

const NextButton = ({ onClick, loading, disabled, children = "Next", sx = {} }) => (
  <Button
    variant="contained"
    onClick={onClick}
    disabled={loading || disabled}
    fullWidth
    sx={{
      mt: 3,
      py: 1.5,
      fontSize: '1.1rem',
      fontWeight: 'bold',
      bgcolor: '#e50914', // Netflix red
      textTransform: 'none',
      borderRadius: '4px',
      '&:hover': { 
        bgcolor: '#b2070f' // Darker Netflix red on hover
      },
      '&:active': {
        transform: 'scale(0.98)'
      },
      boxShadow: 'none', // No shadow for clean look
      ...sx
    }}
  >
    {loading ? <CircularProgress size={24} color="inherit" /> : children}
  </Button>
);

// --- Custom simplified header without hamburger menu ---
const SimpleHeader = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'white', 
        boxShadow: isMobile ? '0px 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
        zIndex: 1100,
        top: 0,
        left: 0,
        right: 0,
        width: '100%'
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Logo */}
        <Box
          component="img"
          src={headerLogo}
          alt="Screamify"
          sx={{
            height: { xs: '25px', sm: '30px' },
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        />
        
        {/* Sign In Button - always shown on Join page */}
        <Button
          variant="contained"
          color="error"
          sx={{
            bgcolor: '#e50914',
            '&:hover': {
              bgcolor: '#b2070f'
            },
            minWidth: { xs: '80px', sm: '100px' },
            height: { xs: '32px', sm: '36px' },
            whiteSpace: 'nowrap',
            fontWeight: 700,
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            textTransform: 'none',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
            WebkitTapHighlightColor: 'transparent',
            color: 'white',
            '&:active': {
              bgcolor: '#990000',
              transform: 'scale(0.98)'
            }
          }}
          onClick={() => navigate('/login?activeTab=0')}
        >
          Sign In
        </Button>
      </Toolbar>
    </AppBar>
  );
};

// --- Custom Netflix-style white footer component ---
const WhiteFooter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        backgroundColor: 'white',
        color: '#333',
        py: isMobile ? 4 : 5,
        textAlign: 'left',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        mt: 'auto',
        position: 'relative',
        zIndex: 5
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              letterSpacing: '0.05em',
              fontSize: '0.9rem',
              mb: 2,
              color: '#333'
            }}
          >
            Need to contact us? Email us at <Link href="mailto:info@screamify.com" sx={{ color: '#333', '&:hover': { color: '#e50914' } }}>info@screamify.com</Link>
          </Typography>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 2 } }}>
              <Link 
                href="/terms" 
                underline="always" 
                sx={{ 
                  color: '#333', 
                  opacity: 0.8, 
                  '&:hover': { opacity: 1, color: '#e50914' },
                  textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                Terms of Service
              </Link>
              
              {!isMobile && (
                <>
                  <Link 
                    href="/faq" 
                    underline="always" 
                    sx={{ 
                      color: '#333', 
                      opacity: 0.8, 
                      '&:hover': { opacity: 1, color: '#e50914' },
                      textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    FAQ
                  </Link>
                  
                  
                </>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 2 } }}>
              <Link 
                href="/privacy" 
                underline="always" 
                sx={{ 
                  color: '#333', 
                  opacity: 0.8, 
                  '&:hover': { opacity: 1, color: '#e50914' },
                  textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                Privacy Policy
              </Link>
              
              {isMobile ? (
                <Link 
                  href="/faq" 
                  underline="always" 
                  sx={{ 
                    color: '#333', 
                    opacity: 0.8, 
                    '&:hover': { opacity: 1, color: '#e50914' },
                    textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  FAQ
                </Link>
              ) : (
                <>
                  <Link 
                    href="/blog" 
                    underline="always" 
                    sx={{ 
                      color: '#333', 
                      opacity: 0.8, 
                      '&:hover': { opacity: 1, color: '#e50914' },
                      textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    Blog
                  </Link>
                  
                  <Link 
                    href="/subscribe" 
                    underline="always" 
                    sx={{ 
                      color: '#333', 
                      opacity: 0.8, 
                      '&:hover': { opacity: 1, color: '#e50914' },
                      textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    Subscribe
                  </Link>
                </>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 2 } }}>
              <Link 
                href="/about" 
                underline="always" 
                sx={{ 
                  color: '#333', 
                  opacity: 0.8, 
                  '&:hover': { opacity: 1, color: '#e50914' },
                  textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                About
              </Link>
              
              {isMobile && (
                <>
                  
                  
                  <Link 
                    href="/blog" 
                    underline="always" 
                    sx={{ 
                      color: '#333', 
                      opacity: 0.8, 
                      '&:hover': { opacity: 1, color: '#e50914' },
                      textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    Blog
                  </Link>
                  
                  <Link 
                    href="/subscribe" 
                    underline="always" 
                    sx={{ 
                      color: '#333', 
                      opacity: 0.8, 
                      '&:hover': { opacity: 1, color: '#e50914' },
                      textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    Subscribe
                  </Link>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        
        <Typography 
          variant="body2" 
          sx={{ 
            opacity: 0.7, 
            fontSize: '0.8rem',
            color: '#555'
          }}
        >
          © {new Date().getFullYear()} Screamify. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

// --- Main Join Component ---

function Join() {
  // Start at the new step 0
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    selectedPlan: PRICE_IDS.member, // Default to Member plan Price ID
    userId: null, // Store userId after signup
    uuid: '', // Store UUID from scannable
    source: '' // Store source (e.g., 'scan')
  });
  const [displayNameAvailable, setDisplayNameAvailable] = useState(null);
  const [isCheckingDisplayName, setIsCheckingDisplayName] = useState(false);
  const { signUp, confirmSignUp, signIn, user, isAuthenticated, isLoading, checkAuthState } = useAuthService();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  // Add a flag to track if auth check has been performed
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Add state for the confirmation code input
  const [confirmationCode, setConfirmationCode] = useState('');
  // Add state to track if confirmation is required (set after signup)
  const [needsConfirmation, setNeedsConfirmation] = useState(false);

  // Add state for password visibility (default to visible)
  const [showPassword, setShowPassword] = useState(true);
  
  // Add state for selected plan (separate from formData for UI feedback)
  const [selectedPlan, setSelectedPlan] = useState('member'); // 'free' or 'member'
  
  // Add state for promo code functionality
  const [promoCode, setPromoCode] = useState('');
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [promoRevealed, setPromoRevealed] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  // Get parameters from URL including email, UUID, and source
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get('email');
    const uuidFromUrl = params.get('uuid');
    const sourceFromUrl = params.get('source');
    const promoFromUrl = params.get('promo');
    
    // Update form data with URL parameters
    setFormData(prev => ({
      ...prev,
      email: emailFromUrl || '',
      uuid: uuidFromUrl || '',
      source: sourceFromUrl || ''
    }));
    
    // Set promo code if provided in URL
    if (promoFromUrl && isPromoEnabled()) {
      setPromoCode(promoFromUrl.toUpperCase());
      // Auto-expand promo code section if valid promo code is provided
      if (isValidPromo(promoFromUrl)) {
        setShowPromoCode(true);
      }
    }
    
    // Only skip welcome screen if we have an email
    // For scannable users, always show welcome screen with promo
    if (emailFromUrl && !(sourceFromUrl === 'scan')) {
      setShowWelcomeScreen(false); // Skip welcome screen only for email
      setActiveStep(0);
    } else {
      // Show welcome screen for scannable users and everyone else
      setShowWelcomeScreen(true);
    }
    
    // Track scannable visit
    // tracking removed
  }, [location.search]);

  // If a valid promo code is entered while Free tier is enabled, force-select Member
  useEffect(() => {
    if (FREE_TIER_ENABLED && isValidPromo(promoCode) && selectedPlan === 'free') {
      setSelectedPlan('member');
    }
  }, [promoCode, selectedPlan]);

  // Optimize auth check to only run once initially and when auth state changes
  useEffect(() => {
    // tracking removed

    // Only check auth if it hasn't been checked yet or if auth state has changed
    if (!hasCheckedAuth || isAuthenticated === null) {
      checkAuthState();
      setHasCheckedAuth(true);
    }

    // If the user is already authenticated, drop them into the plan selection flow (promo-capable)
    if (!isLoading && isAuthenticated && activeStep <= 1 && !needsConfirmation) {
      setShowWelcomeScreen(false);
      setActiveStep(3); // Step 3: Choose Plan; Step 4 has promo code
    }
  }, [isAuthenticated, isLoading, navigate, activeStep, needsConfirmation, checkAuthState, hasCheckedAuth]);

  // --- Display Name Check (Copied from Login.jsx) ---
  const checkDisplayNameAvailability = async (name) => {
    if (!name || name.length < 3) {
      setDisplayNameAvailable(null);
      return;
    }

    setIsCheckingDisplayName(true);
    setDisplayNameAvailable(null); // Reset while checking
    try {
      console.log(`Checking availability for: "${name}" in Join funnel`);

      const response = await fetch('https://zm6jvy90l6.execute-api.us-east-1.amazonaws.com/scan-display-names', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ displayName: name })
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      console.log('Display name check API response (Join):', data);

      // Set display name availability based on the response
      setDisplayNameAvailable(data.isAvailable);
    } catch (error) {
      console.error('Error checking display name (Join):', error);
      // Treat as unavailable on error to be safe
      setDisplayNameAvailable(false);
      setError("Could not verify display name. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsCheckingDisplayName(false);
    }
  };

  const handleNext = async () => {
    setError(null); // Clear previous errors
    setLoading(true);
    // tracking removed

    try {
      // Handle the new Step 0 (Setup)
      if (activeStep === 0) {
        // If this is from a scannable, track it
        // tracking removed
        // Just move to the next step (Create Account)
        setActiveStep(1);
        setLoading(false); // Stop loading immediately
        return; // Exit handleNext
      } else if (activeStep === 1) { // Create Account (was 0)
        if (!formData.email || !formData.password) {
          throw new Error("Email and password are required.");
        }
        if (!formData.displayName) {
          throw new Error("Display name is required.");
        }
        if (formData.displayName.length < 3) {
          throw new Error("Display name must be at least 3 characters long.");
        }
        // Re-check availability just before submitting, in case it changed
        await checkDisplayNameAvailability(formData.displayName);
        if (displayNameAvailable === false) { // Check the state variable after await
          throw new Error("Display name is not available. Please choose another.");
        }

        // tracking removed
        const signUpResult = await signUp(formData.email, formData.password, formData.displayName, formData.uuid);
        if (signUpResult && signUpResult.userId) {
            setFormData(prev => ({ ...prev, userId: signUpResult.userId }));
            // tracking removed

            // Check if confirmation is needed based on Cognito response
            const needsConfirm = signUpResult.nextStep?.signUpStep === 'CONFIRM_SIGN_UP';
            setNeedsConfirmation(needsConfirm);

            if (needsConfirm) {
                // Move to Step 2 (Verify Email with code) - was Step 1
                setActiveStep(2);
            } else {
                // Auto-confirmed, try to sign in and move to Step 3 (Choose Plan) - was Step 2
                await checkAuthState(); // Ensure auth state is updated
                try {
                    await signIn(formData.email, formData.password);
                    console.log("signIn successful after confirmation (Join.jsx)");
                    // tracking removed

                    // Set active step directly now (removed setTimeout)
                    // Move to Step 3 (Choose Plan) after successful sign in - was Step 2
                    console.log("Setting active step to 3 (Choose Plan) after sign in (Join.jsx)");
                    setActiveStep(3);
                } catch (signInError) {
                    // Should not happen if auto-confirmed, but handle defensively
                    console.error("Sign-in failed after auto-confirmation:", signInError);
                    setError("Account created, but sign-in failed. Please try logging in.");
                    navigate('/login?email=' + formData.email); // Redirect to login
                }
            }
        } else {
            throw new Error(signUpResult?.message || 'Sign up failed. The email might already be in use.');
        }
      } else if (activeStep === 2) { // Verify Email with Code (was 1)
        // Logic for verification remains the same, step index changed
        // The content for this step is rendered via renderStepContent(2)
        // The actual verification logic is in handleConfirmationSubmit
        // No direct action needed here in handleNext for this specific step
        // We need the user to submit the form first. handleNext shouldn't proceed.
        console.log("handleNext reached for Verify Email step, but action requires form submission.");
        setLoading(false); // Stop loading as we wait for user input
        return; // Wait for handleConfirmationSubmit
      } else if (activeStep === 3) { // Choose Plan (Step 3 of 4)
        // Simply move to the next step (Step 4) to select a specific plan
        setActiveStep(4);
        setLoading(false);
        return;
      } else if (activeStep === 4) { // Plan Selection (Step 4 of 4)
        // Validate plan selection
        if (!formData.selectedPlan) {
          throw new Error("Please select a plan before continuing.");
        }
        
        // tracking removed

        // Set plan type to member
        let planType = 'member';

        // Update the user's planType in DynamoDB using the direct POST method
        try {
          console.log(`Join.jsx: Calling updateUserPlanType with userId=${formData.userId}, planType=${planType}`);
          const result = await updateUserPlanType(formData.userId, planType);
          console.log('Join.jsx: Plan type update result:', result);
        } catch (dbError) {
          console.error('Error updating plan type in DynamoDB from Join.jsx:', dbError);
          // Continue with subscription process even if this fails
        }

        // For regular members, process the subscription
        try {
          const customer = await createCustomer(formData.email, formData.userId);
          if (!customer || !customer.id) {
            throw new Error('Could not create or retrieve customer information.');
          }
          
          // tracking removed
          
          // Proceed to Stripe Checkout
          const metadata = {
            userId: formData.userId,
            email: formData.email,
            displayName: formData.displayName,
            planType: planType
          };
          
          // Add UUID to metadata if present
          if (formData.uuid) {
            metadata.uuid = formData.uuid;
          }
          
          // Check if promo code is valid for trial
          const trialDays = (isPromoEnabled() && isValidPromo(promoCode) && selectedPlan === 'member') ? getPromoTrialDays() : 0;
          
          // Add promo code to metadata if used
          if (trialDays > 0) {
            metadata.promoCode = promoCode;
            // tracking removed
          }
          
          // tracking removed
          
          await createSubscription(formData.email, formData.userId, formData.selectedPlan, metadata, trialDays);
          // tracking removed
        } catch (stripeError) {
          console.error("Stripe process error:", stripeError);
          throw new Error("Payment processing failed. Please try again or contact support.");
        }
      }
    } catch (err) {
      console.error(`Error in step ${activeStep}:`, err);
      let errorMessage = err.message || `An error occurred during step ${activeStep + 1}.`;

      // Provide a more user-friendly message for existing users
      if (activeStep === 1 && err.name === 'UsernameExistsException') { // Check step 1 (Create Account)
          errorMessage = "An account with this email already exists. Please try signing in.";
          // Optional: redirect to login or highlight sign-in option
          // navigate('/login?email=' + formData.email);
      }

      setError(errorMessage);
      setSnackbarOpen(true);
      // tracking removed
    } finally {
      setLoading(false);
    }
  };

  // --- Confirmation Code Submission (Adapted from Login.jsx) ---
  const handleConfirmationSubmit = async (e) => {
    e.preventDefault();
    console.log("handleConfirmationSubmit called (Join.jsx)");
    setLoading(true);
    setError("");
    // tracking removed

    try {
      console.log(`Attempting confirmSignUp for ${formData.email} with code ${confirmationCode} (Join.jsx)`);
      // Confirm the sign up using the code
      await confirmSignUp(formData.email, confirmationCode);
      console.log("confirmSignUp successful (Join.jsx)");
      // tracking removed

      // After successful confirmation, attempt to sign in the user automatically
      try {
        console.log(`Attempting signIn for ${formData.email} after confirmation (Join.jsx)`);
        await signIn(formData.email, formData.password);
        console.log("signIn successful after confirmation (Join.jsx)");
        // tracking removed

        // Set active step directly now (removed setTimeout)
        // Move to Step 3 (Choose Plan) after successful sign in - was Step 2
        console.log("Setting active step to 3 (Choose Plan) after sign in (Join.jsx)");
        setActiveStep(3);

      } catch (signInError) {
        // If sign-in fails after confirmation (e.g., network issue), handle it.
        // Send user to login page as confirmation succeeded but session couldn't start.
        console.error("Sign-in error after confirmation (Join.jsx):", signInError);
        console.error("Sign-in failed after confirmation:", signInError);
        setError("Verification successful, but automatic sign-in failed. Please sign in manually.");
        setSnackbarOpen(true); // Ensure snackbar opens for this error
        // Optionally redirect to login, pre-filling email
        navigate(`/login?email=${encodeURIComponent(formData.email)}`);
      }

    } catch (error) {
      console.error("Confirmation error caught (Join.jsx):", error);
      console.error("Confirmation error:", error);
      setError(error.message || "Verification failed. Please check the code and try again.");
      setSnackbarOpen(true);
      // tracking removed
    } finally {
      console.log("handleConfirmationSubmit finally block (Join.jsx)");
      setLoading(false);
    }
  };

  const handleBack = () => {
    // No-op function - back functionality disabled
    console.log("Back button functionality has been disabled");
    // We're keeping this function to avoid breaking any code that might call it
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Trigger display name check when the displayName field changes
    if (name === 'displayName') {
      // Only check names 3+ characters long
      if (value.length >= 3) {
        // If displayName is available, set it to true immediately for better UX
        // This lets the Next button enable faster
        if (value !== formData.displayName) {
          // Reset only when actually changing to a new name
          setDisplayNameAvailable(null);
        }
        // Debounce the API call to check availability
        const timeoutId = setTimeout(() => {
          checkDisplayNameAvailability(value);
        }, 500);
        return () => clearTimeout(timeoutId);
      } else {
        setDisplayNameAvailable(null); // Reset if too short
      }
    }
  };

  const handlePlanChange = (event) => {
    setFormData(prev => ({ ...prev, selectedPlan: event.target.value }));
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  const handleCloseCopySnackbar = () => {
    setCopySnackbarOpen(false);
  };

  // Event handler for buttons on welcome screen - improved for reliability
  const handleWelcomeAction = (action) => {
    console.log(`Welcome action triggered: ${action}`);
    try {
      if (action === 'create') {
        setShowWelcomeScreen(false);
        setActiveStep(0);
      } else if (action === 'manage') {
        navigate('/login?activeTab=0');
      }
    } catch (error) {
      console.error("Navigation error:", error);
      setError("There was an error processing your request. Please try again.");
      setSnackbarOpen(true);
    }
  };

  // Utility: copy provided text to clipboard with fallback for older browsers
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      console.log('Promo code copied to clipboard');
      setCopySnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy promo code:', err);
    }
  };

  // Welcome Screen Component with more stable image rendering
  const WelcomeScreen = () => {
    const PROMO_BUTTON_WIDTH = isMobile ? 280 : 320;
    const PROMO_BUTTON_HEIGHT = 44;

    return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        py: isMobile ? 3 : 6,
        px: 2,
        textAlign: 'center'
      }}
    >
      {/* Popcorn image with optimized rendering properties to prevent flickering */}
      <Box
        sx={{
          width: { xs: '110px', sm: '150px' },
          height: { xs: '110px', sm: '150px' },
          mb: { xs: 2, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          backfaceVisibility: 'hidden',
          perspective: 1000,
          transform: 'translateZ(0)',
          willChange: 'transform',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }}
      >
        <Box 
          component="img"
          src={logo}
          alt="Horror Popcorn"
          loading="eager"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            position: 'absolute',
            top: 0,
            left: 0,
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            WebkitTransform: 'translate3d(0, 0, 0)',
            transform: 'translate3d(0, 0, 0)',
            WebkitTapHighlightColor: 'transparent',
            animation: 'none',
            transition: 'none'
          }}
        />
      </Box>

      {/* Main message */}
      <Typography variant="h3" component="h1" gutterBottom 
        sx={{ 
          fontWeight: 'bold', 
          color: '#111',
          mb: { xs: 2, sm: 4 },
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          textAlign: 'center',
          lineHeight: 1.2
        }}
      >
        What would you like to <br />do today?
      </Typography>

      {/* Action Buttons with more reliable click handling */}
      <Box sx={{ width: '100%', maxWidth: '450px' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            console.log("Create account button clicked");
            handleWelcomeAction('create');
          }}
          type="button"
          sx={{
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 1.5, sm: 2 },
            fontSize: '1.2rem',
            fontWeight: 'bold',
            bgcolor: '#e50914',
            textTransform: 'none',
            borderRadius: '4px',
            '&:hover': { 
              bgcolor: '#b2070f' 
            },
            boxShadow: '0 1px 5px rgba(0, 0, 0, 0.2)',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          Create a Screamify Account
        </Button>
        
        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            console.log("Manage account button clicked");
            handleWelcomeAction('manage');
          }}
          type="button"
          sx={{
            py: { xs: 1.5, sm: 2 },
            fontSize: '1.2rem',
            fontWeight: 'bold',
            bgcolor: '#e6e6e6',
            color: '#333',
            textTransform: 'none',
            borderRadius: '4px',
            '&:hover': { 
              bgcolor: '#d5d5d5' 
            },
            boxShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          Manage Account
        </Button>
      </Box>
      
      {/* Promo code message - simplified, high-end style */}
      {isPromoEnabled() && (
        <Box sx={{ width: '100%', maxWidth: 450, mx: 'auto', mt: { xs: 2, sm: 3 } }}>
          <Box sx={{
            bgcolor: '#fff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <Typography
              variant="overline"
              sx={{
                color: '#e50914',
                letterSpacing: '.2em',
                fontFamily: 'Creepster, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                fontSize: { xs: '1.6rem', sm: '1.9rem' },
                lineHeight: 1.05,
                display: 'block',
                transform: 'translateY(1px)',
                mb: 1
              }}
            >
              Limited-time offer
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#111',
                mt: 1,
                mb: 1.5,
                fontSize: { xs: '1.25rem', sm: '1.35rem' },
                lineHeight: 1.2
              }}
            >
              Free {getPromoTrialDays()} day trial
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.72)', mb: 2 }}>
              Use this code during sign up
            </Typography>
            {!promoRevealed ? (
              <Button
                onClick={async () => {
                  setPromoRevealed(true);
                  await copyToClipboard(getPromoCode());
                }}
                variant="contained"
                color="error"
                sx={{
                  width: PROMO_BUTTON_WIDTH,
                  height: PROMO_BUTTON_HEIGHT,
                  boxSizing: 'border-box',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 0,
                  boxShadow: '0 6px 14px rgba(229, 9, 20, 0.18)',
                  mb: 1.5
                }}
              >
                Unlock promo code
              </Button>
            ) : null}
            {promoRevealed && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: PROMO_BUTTON_WIDTH,
              height: PROMO_BUTTON_HEIGHT,
              boxSizing: 'border-box',
              mb: 1.5,
              borderRadius: 0,
              position: 'relative',
              backgroundColor: '#e50914',
              backgroundImage: 'linear-gradient(90deg, #ff3b3b 0%, #e50914 50%, #b30606 100%)',
              color: '#fff',
              border: '1px solid #b30606',
              boxShadow: '0 6px 14px rgba(229, 9, 20, 0.25)',
              fontWeight: 800,
              letterSpacing: '.14em',
              fontSize: '0.95rem',
              textTransform: 'uppercase',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 0,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.0) 60%)',
                pointerEvents: 'none'
              }
            }}>
              {getPromoCode()}
            </Box>
            )}
            {getPromoDaysRemaining() !== null && getPromoDaysRemaining() > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: { xs: 1.5, sm: 2 }, color: '#e50914', fontWeight: 700 }}>
                Ends in {getPromoDaysRemaining()} days
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Highlight Feature Cards - removed from first page */}
      {/* <Box sx={{ mt: 4, width: '100%', maxWidth: '450px' }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              border: '2px solid rgba(229,9,20,0.5)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <MovieFilterIcon sx={{ color: '#e50914' }} />
              <Typography sx={{ fontWeight: 'bold', color: '#111' }}>Over 250 Movies</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              border: '2px solid rgba(229,9,20,0.5)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <StarIcon sx={{ color: '#e50914' }} />
              <Typography sx={{ fontWeight: 'bold', color: '#111' }}>Screamify Originals</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              border: '2px solid rgba(229,9,20,0.5)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <GraphicEqIcon sx={{ color: '#e50914' }} />
              <Typography sx={{ fontWeight: 'bold', color: '#111' }}>Podcasts and more</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ textAlign: 'center', color: '#e50914', fontWeight: 'bold' }}>
              All for $2.99/month
            </Typography>
          </Grid>
        </Grid>
      </Box> */}

      {/* Devices Gallery below promo banner - moved to Step 1 */}
      {/* <Box sx={{ mt: 4, width: '100%', maxWidth: '450px' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111', mb: 2, textAlign: 'center' }}>
          Available on all your devices
        </Typography>
        <Grid container spacing={1}>
          {[device1, device2, device3, device4, device5, device6].map((img, idx) => (
            <Grid item xs={6} key={idx}>
              <Box
                component="img"
                src={img}
                alt={`Screamify devices ${idx + 1}`}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  display: 'block',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box> */}
    </Box>
    );
  };

  // Add function to toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: // New Setup Step (Netflix Clone)
        return (
          <StepContentWrapper>
            <Box sx={{ textAlign: 'left', mb: { xs: 2.5, sm: 4 } }}>
              <Typography variant="caption" display="block" sx={{ color: '#555', mb: { xs: 0.5, sm: 1 }, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                STEP 1 OF 4
              </Typography>

              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2rem' } }}>
                Create a password to start your membership
              </Typography>

              <Typography variant="body1" sx={{ color: '#555', mb: { xs: 2, sm: 3 }, maxWidth: '400px' }}>
                Just a few more steps and you're done! We hate paperwork, too.
              </Typography>

              <NextButton onClick={handleNext} loading={loading}>
                Next
              </NextButton>
              
              {/* Devices Gallery below the promo card, consistent with landing/central */}
              <Box sx={{ mt: { xs: 2.5, sm: 5 }, display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#111', mb: 2, textAlign: 'center' }}>
                  Available on all your devices
                </Typography>
                <Grid container spacing={2}>
                  {[device1, device2, device3, device4, device5, device6].map((img, idx) => (
                    <Grid item xs={6} sm={6} key={idx}>
                      <Box
                        component="img"
                        src={img}
                        alt={`Screamify devices ${idx + 1}`}
                        loading="lazy"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: 2,
                          display: 'block',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </StepContentWrapper>
        );
      case 1: // Create Account (was 0)
        return (
          <StepContentWrapper>
            <Typography variant="caption" display="block" sx={{ color: '#555', mb: { xs: 0.5, sm: 1 }, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              STEP 1 OF 4
            </Typography>

            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2rem' } }}>
              Create a password to start your membership
            </Typography>

            <Typography variant="body1" sx={{ color: '#555', mb: { xs: 2, sm: 3 } }}>
              Just a few more steps and you're done! We hate paperwork, too.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography 
                component="label" 
                htmlFor="email" 
                sx={{ 
                  display: 'block', 
                  mb: 1, 
                  ml: 1, 
                  fontSize: '0.75rem', 
                  color: 'rgba(0, 0, 0, 0.6)'
                }}
              >
                Email *
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-form-type="other"
                  className="no-browser-icon"
                  style={{
                    width: '100%',
                    padding: '14px 14px',
                    fontSize: '1rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.setAttribute('autocomplete', 'new-password');
                    e.target.style.borderColor = '#e50914';
                    e.target.style.borderWidth = '2px';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                    e.target.style.borderWidth = '1px';
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography 
                component="label" 
                htmlFor="displayName" 
                sx={{ 
                  display: 'block', 
                  mb: 1, 
                  ml: 1, 
                  fontSize: '0.75rem', 
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: displayNameAvailable !== null ? 'bold' : 'normal'
                }}
              >
                Choose a Display Name *
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    e.target.setAttribute('autocomplete', 'new-password');
                    e.target.style.borderColor = '#e50914';
                    e.target.style.borderWidth = '2px';
                  }}
                  onBlur={(e) => {
                    if (displayNameAvailable === false) {
                      e.target.style.borderColor = '#e50914';
                      e.target.style.borderWidth = '2px';
                    } else {
                      e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
                      e.target.style.borderWidth = '1px';
                    }
                  }}
                  required
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-form-type="other"
                  style={{
                    width: '100%',
                    padding: '14px 14px',
                    paddingRight: '110px', // Space for status text
                    fontSize: '1rem',
                    borderRadius: '4px',
                    border: displayNameAvailable === false
                      ? '2px solid #e50914'
                      : '1px solid rgba(0, 0, 0, 0.23)',
                    backgroundColor: 'white',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    outline: 'none',
                    caretColor: '#e50914',
                    transition: 'all 0.3s ease',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: 'none'
                  }}
                  className="no-browser-icon"
                />
                <Box
                  component="style"
                  dangerouslySetInnerHTML={{
                    __html: `
                      .no-browser-icon::-webkit-contacts-auto-fill-button,
                      .no-browser-icon::-webkit-credentials-auto-fill-button {
                        visibility: hidden;
                        display: none !important;
                        pointer-events: none;
                        position: absolute;
                        right: 0;
                      }
                      .no-browser-icon::-webkit-calendar-picker-indicator {
                        display: none !important;
                        opacity: 0;
                      }
                      .no-browser-icon::-webkit-inner-spin-button { 
                        display: none !important;
                        opacity: 0;
                      }
                      .no-browser-icon::-webkit-search-decoration {
                        display: none !important;
                      }
                      .no-browser-icon::-webkit-search-cancel-button {
                        display: none !important;
                      }
                      .no-browser-icon::-webkit-search-results-button {
                        display: none !important;
                      }
                      .no-browser-icon::-webkit-search-results-decoration {
                        display: none !important;
                      }
                    `
                  }}
                />
                {isCheckingDisplayName && (
                  <CircularProgress 
                    size={20} 
                    thickness={5} 
                    sx={{ 
                      color: 'rgba(0, 0, 0, 0.5)',
                      position: 'absolute',
                      right: '15px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }} 
                  />
                )}
                {!isCheckingDisplayName && displayNameAvailable === true && (
                  <span style={{ 
                    color: '#000', 
                    position: 'absolute', 
                    right: '15px',
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    zIndex: 1
                  }}>
                    Available
                  </span>
                )}
                {!isCheckingDisplayName && displayNameAvailable === false && (
                  <span style={{ 
                    color: '#e50914', 
                    position: 'absolute', 
                    right: '15px',
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    zIndex: 1
                  }}>
                    Taken
                  </span>
                )}
              </Box>
            </Box>
            
            <TextField
              label="Add a password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              required
              helperText="Password must contain at least one uppercase letter and one special character"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      sx={{ 
                        color: 'rgba(0, 0, 0, 0.54)',
                        '&:hover': {
                          color: '#e50914'
                        },
                        '&:focus, &:focus-visible': {
                          outline: 'none',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '&:focus': {
                    outline: 'none',
                  }
                }
              }}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white', 
                  borderRadius: '4px',
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderWidth: '1px',
                    boxShadow: 'none',
                    outline: 'none'
                  },
                  '&:focus': {
                    outline: 'none',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'rgba(0, 0, 0, 0.6)',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23) !important',
                  borderWidth: '1px !important'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23) !important',
                  borderWidth: '1px !important'
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.875rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  mt: 0.5,
                  ml: 1
                }
              }}
            />
            
            <NextButton
              onClick={handleNext}
              loading={loading}
              disabled={!formData.email || !formData.password || !formData.displayName || 
                (displayNameAvailable === false) || 
                (formData.displayName.length >= 3 && displayNameAvailable === null && !isCheckingDisplayName)}
              sx={{ mt: { xs: 2, sm: 3 } }}
            >
              Next
            </NextButton>
          </StepContentWrapper>
        );
      case 2: // Verify Email with Code (was 1)
        // Render this step only if confirmation is actually needed
        if (!needsConfirmation) return null;
        return (
          <StepContentWrapper>
            <Typography variant="caption" display="block" sx={{ color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
              STEP 2 OF 4
            </Typography>
            
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: 2, textAlign: 'left' }}>
              Verify Your Email
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: '#555', textAlign: 'left' }}>
              We've sent a confirmation code to your email. Please enter it below to verify your account.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#555',
                  mb: 1,
                  fontWeight: 'bold',
                  textAlign: 'left'
                }}
              >
                Confirmation Code
              </Typography>
              <TextField
                type="text"
                name="confirmationCode"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmationSubmit}
                disabled={loading}
                sx={{
                  py: 1.5,
                  backgroundColor: '#e50914',
                  '&:hover': {
                    backgroundColor: '#b2070f'
                  },
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  borderRadius: '4px',
                  boxShadow: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verify"}
              </Button>
            </Box>
          </StepContentWrapper>
        );
      case 3: // Choose Plan (was 2)
        return (
          <StepContentWrapper>
            <Box sx={{ textAlign: 'left' }}>
              <Box sx={{ display: 'flex', mb: 4 }}>
                <CheckCircleOutlineIcon sx={{ color: '#e50914', fontSize: 56, mr: 2 }} />
              </Box>
              
              <Typography variant="caption" display="block" sx={{ color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                STEP 3 OF 4
              </Typography>
              
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: 4, textAlign: 'left' }}>
                Choose your plan.
              </Typography>
              
              <Button
                onClick={handleNext}
                disabled={loading}
                fullWidth
                variant="contained"
                sx={{
                  py: 1.5,
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  bgcolor: '#e50914',
                  textTransform: 'none',
                  borderRadius: '4px',
                  '&:hover': { 
                    bgcolor: '#b2070f'
                  },
                  '&:active': {
                    transform: 'scale(0.98)'
                  },
                  boxShadow: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Next"}
              </Button>
            </Box>
          </StepContentWrapper>
        );
       case 4: // Plan Selection (Step 4 of 4)
         return (
           <StepContentWrapper>
             <Box sx={{ textAlign: 'left', pt: { xs: 1, sm: 0 } }}>
               <Typography variant="caption" display="block" sx={{ color: '#555', mb: { xs: 0.5, sm: 1 }, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>
                 STEP 4 OF 4
               </Typography>
               
               <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#333', mb: { xs: 2, sm: 4 }, textAlign: 'left', fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
                 Choose your plan
               </Typography>
               
               {/* Plan Selection Cards */}
               <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 }, mb: { xs: 2.5, md: 4 } }}>
                 {/* Free Plan */}
                 {FREE_TIER_ENABLED && (
                 <Box 
                   onClick={() => setSelectedPlan('free')}
                   sx={{ 
                     flex: 1, 
                     border: selectedPlan === 'free' ? '2px solid #6a5acd' : '2px solid #6c757d',
                     borderRadius: 2,
                     p: { xs: 2, md: 3 },
                     position: 'relative',
                     backgroundColor: selectedPlan === 'free' ? 'rgba(106, 90, 205, 0.05)' : '#f8f9fa',
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: selectedPlan === 'free' ? 'rgba(106, 90, 205, 0.08)' : '#e9ecef',
                       transform: 'translateY(-1px)'
                     }
                   }}
                 >
                   <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Free</Typography>
                   <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#6a5acd', mb: 2 }}>$0/month</Typography>
                   <Typography variant="body1" sx={{ mb: 1, fontWeight: 'medium' }}>✓ Horror Podcasts</Typography>
                   <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6c757d', fontStyle: 'italic', mb: 1.5 }}>
                     No movies or originals
                   </Typography>
                   <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', fontWeight: 'medium' }}>
                     Requires Screamify app
                   </Typography>
                   <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#888' }}>
                     iOS + Android mobile
                   </Typography>
                   
                   {selectedPlan === 'free' && (
                     <Box 
                       sx={{ 
                         position: 'absolute', 
                         top: 10, 
                         right: 10, 
                         width: 24, 
                         height: 24, 
                         borderRadius: '50%', 
                         bgcolor: '#6a5acd',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center'
                       }}
                     >
                       <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                     </Box>
                   )}
                 </Box>
                 )}

                 {/* Member Plan */}
                 <Box 
                   onClick={() => setSelectedPlan('member')}
                   sx={{ 
                     flex: 1, 
                     border: selectedPlan === 'member' ? '2px solid #e50914' : '2px solid #6c757d',
                     borderRadius: 2,
                     p: { xs: 2, md: 3 },
                     position: 'relative',
                     backgroundColor: selectedPlan === 'member' ? 'rgba(229, 9, 20, 0.05)' : '#f8f9fa',
                     cursor: 'pointer',
                     transition: 'all 0.2s ease',
                     '&:hover': {
                       backgroundColor: selectedPlan === 'member' ? 'rgba(229, 9, 20, 0.08)' : '#e9ecef',
                       transform: 'translateY(-1px)'
                     }
                   }}
                 >
                   <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Member</Typography>
                   <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#e50914', mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.4rem', sm: '1.75rem' } }}>
                    {isPromoEnabled() && isValidPromo(promoCode) ? (
                      <>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '1.5rem' }}>$2.99</span>
                        {' '}FREE for {getPromoTrialDays()} days
                      </>
                    ) : (
                      '$2.99/month'
                    )}
                  </Typography>
                  {isPromoEnabled() && isValidPromo(promoCode) && (
                   <Typography variant="body2" sx={{ color: '#e50914', fontWeight: 'bold', mb: { xs: 1.5, sm: 2 } }}>
                      {landingSettings.promo.description}
                    </Typography>
                  )}
                   <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 'medium', fontSize: { xs: '0.95rem', sm: '1rem' } }}>✓ Hand Picked Horror Movies</Typography>
                   <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 'medium', fontSize: { xs: '0.95rem', sm: '1rem' } }}>✓ Horror Podcasts</Typography>
                   <Typography variant="body1" sx={{ mb: 1.25, fontWeight: 'medium', fontSize: { xs: '0.95rem', sm: '1rem' } }}>✓ Screamify Originals</Typography>
                   <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: '#666', fontWeight: 'medium' }}>
                     Requires Screamify app
                   </Typography>
                   <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#888' }}>
                     iOS + Android mobile, Amazon Fire TV, Android TV, Apple TV and Roku
                   </Typography>
                   
                   {selectedPlan === 'member' && (
                     <Box 
                       sx={{ 
                         position: 'absolute', 
                         top: 10, 
                         right: 10, 
                         width: 24, 
                         height: 24, 
                         borderRadius: '50%', 
                         bgcolor: '#e50914',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center'
                       }}
                     >
                       <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                     </Box>
                   )}
                 </Box>
               </Box>
               
               {FREE_TIER_ENABLED && (
                 <Box sx={{ mb: 4, textAlign: 'center' }}>
                   <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: '#333' }}>
                     What's the difference?
                   </Typography>
                   
                   <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 4 }}>
                     <Box sx={{ textAlign: 'center' }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6a5acd', mb: 1 }}>
                         Free
                       </Typography>
                       <Typography variant="body2" sx={{ color: '#666' }}>
                         Just podcasts
                       </Typography>
                     </Box>
                     
                     <Box sx={{ textAlign: 'center' }}>
                       <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e50914', mb: 1 }}>
                         Member
                       </Typography>
                       <Typography variant="body2" sx={{ color: '#666' }}>
                         Movies + Podcasts + More
                       </Typography>
                     </Box>
                   </Box>
                 </Box>
               )}
               
               {/* Promo Code Section */}
               {isPromoEnabled() && (
                 <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                   <Box sx={{ textAlign: 'center' }}>
                     <Typography
                       variant="h6"
                       sx={{
                         color: '#e50914',
                         fontFamily: 'Creepster, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                         letterSpacing: '.12em',
                         fontSize: { xs: '1.5rem', sm: '1.75rem' },
                         fontWeight: 700,
                         lineHeight: 1
                       }}
                     >
                       Add promo code
                     </Typography>
                     <Button
                       variant="text"
                       onClick={() => setShowPromoCode(!showPromoCode)}
                       sx={{
                         color: '#111',
                         textTransform: 'none',
                         textDecoration: 'underline',
                         fontSize: '1rem',
                         fontWeight: 700,
                         mt: 0.5,
                         '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                       }}
                     >
                       Click here
                     </Button>
                   </Box>
                   
                   <Collapse in={showPromoCode}>
                     <Box sx={{ mt: 2, maxWidth: '300px', mx: 'auto' }}>
                       <TextField
                         placeholder="Enter promo code"
                         value={promoCode}
                         onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                         fullWidth
                         size="small"
                         InputProps={{
                           endAdornment: isValidPromo(promoCode) && (
                             <CheckCircleIcon sx={{ color: '#ff0000', fontSize: 20 }} />
                           ),
                           sx: {
                             '& input': {
                               textAlign: 'center',
                               fontWeight: 'bold',
                               letterSpacing: '0.05em'
                             }
                           }
                         }}
                         sx={{
                           '& .MuiOutlinedInput-root': {
                             backgroundColor: 'white',
                             '& fieldset': {
                               borderColor: 'rgba(0,0,0,0.25)'
                             },
                             '&:hover fieldset': {
                               borderColor: 'rgba(0,0,0,0.35)'
                             },
                             '&.Mui-focused fieldset': {
                               borderColor: 'rgba(0,0,0,0.45)'
                             }
                           }
                         }}
                       />
                       {isValidPromo(promoCode) && (
                         <>
                           <Typography variant="caption" sx={{ color: '#111', display: 'block', mt: 1, fontWeight: 'bold' }}>
                             {landingSettings.promo.displayText} activated
                           </Typography>
                           {getPromoDaysRemaining() !== null && getPromoDaysRemaining() > 0 && (
                             <Typography variant="caption" sx={{ 
                               color: '#e50914', 
                               display: 'block', 
                               mt: 0.5, 
                               fontWeight: 'bold',
                               fontSize: '0.9rem'
                             }}>
                               Only {getPromoDaysRemaining()} days left!
                             </Typography>
                           )}
                         </>
                       )}
                       {promoCode && !isValidPromo(promoCode) && promoCode.length > 3 && (
                         <Typography variant="caption" sx={{ color: '#e50914', display: 'block', mt: 1 }}>
                           Invalid promo code
                         </Typography>
                       )}
                     </Box>
                   </Collapse>
                 </Box>
               )}
               
               {/* Action Button */}
               <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 1.5, sm: 2 } }}>
                 {selectedPlan === 'free' ? (
                   <Button
                     onClick={async () => {
                       // Free tier users complete the flow - planType set at user creation
                       try {
                         setLoading(true);
                         
                         // Track free tier selection
                         // tracking removed
                         
                         // Capture free tier signup email to Airtable
                         try {
                           await airtableEmailService.captureFreeSignup(formData.email);
                           console.log('Free tier email captured successfully');
                         } catch (airtableError) {
                           // Log error but don't block the user flow
                           console.error('Failed to capture email to Airtable:', airtableError);
                           // Track the error but continue with the flow
                           // tracking removed
                         }
                         
                         
                         // tracking removed
                         
                         // Navigate to Screamify Central as authenticated free user with a refresh indicator
                         navigate('/screamify-central?fromJoin=true');
                       } catch (error) {
                         console.error('Error completing free tier signup:', error);
                         setError('Failed to complete signup. Please try again.');
                         setSnackbarOpen(true);
                       } finally {
                         setLoading(false);
                       }
                     }}
                     disabled={loading}
                     fullWidth
                     variant="outlined"
                     sx={{
                       py: 1.5,
                       fontSize: '1.25rem',
                       fontWeight: 'bold',
                       color: '#6a5acd',
                       borderColor: '#6a5acd',
                       textTransform: 'none',
                       borderRadius: '4px',
                       '&:hover': { 
                         borderColor: '#5b4db5',
                         color: '#5b4db5',
                         bgcolor: 'rgba(106, 90, 205, 0.05)'
                       },
                       '&:active': {
                         transform: 'scale(0.98)'
                       },
                       boxShadow: 'none'
                     }}
                   >
                     {loading ? <CircularProgress size={24} color="inherit" /> : "Start with Free"}
                   </Button>
                 ) : (
                   <Button
                     onClick={handleNext}
                     disabled={loading}
                     fullWidth
                     variant="contained"
                     sx={{
                       py: 1.5,
                       fontSize: '1.25rem',
                       fontWeight: 'bold',
                       bgcolor: '#e50914',
                       textTransform: 'none',
                       borderRadius: '4px',
                       '&:hover': { 
                         bgcolor: '#b2070f'
                       },
                       '&:active': {
                         transform: 'scale(0.98)'
                       },
                       boxShadow: 'none'
                     }}
                   >
                     {loading ? <CircularProgress size={24} color="inherit" /> : "Subscribe to Member"}
                   </Button>
                 )}
               </Box>
             </Box>
           </StepContentWrapper>
         );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };



  return (
    <HelmetProvider>
      <Helmet>
        <title>Join Screamify™ | Support Independent Horror</title>
        <meta name="description" content="Join Screamify and support independent horror filmmakers. Choose your plan and start streaming unique horror content." />
        <meta name="keywords" content="join screamify, horror streaming signup, independent horror, support filmmakers, horror community" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Box
        sx={{
          minHeight: { xs: '100svh', sm: '100vh' },
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <SimpleHeader /> {/* Use the SimpleHeader component instead of Header */}
        <Container component="main" maxWidth="md" sx={{ pt: 12, pb: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 0 auto' }}>
          {showWelcomeScreen ? (
            <WelcomeScreen />
          ) : (
            <>
              {/* Remove the main title */}
              
              {/* Remove the Stepper component */}
              
              <Box sx={{ maxWidth: 600, mx: 'auto', flex: '1 0 auto', width: '100%', display: 'flex', flexDirection: 'column' }}>
                {renderStepContent(activeStep)}
              </Box>

              {/* Back Button removed to prevent users from going backwards */}
            </> 
          )}
        </Container>
         <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
          <Snackbar open={copySnackbarOpen} autoHideDuration={3000} onClose={handleCloseCopySnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert onClose={handleCloseCopySnackbar} severity="success" sx={{ width: '100%' }}>
              Promo code copied!
            </Alert>
          </Snackbar>
        
        {/* Use the custom white footer instead of the imported Footer */}
        <WhiteFooter />
      </Box>
    </HelmetProvider>
  );
}

export default Join;